import { FiChevronRight } from 'react-icons/fi';
import styled from 'styled-components';
import { KanoPredictionResult } from '../../ai/useKanoPrediction';
import { ThemeButton } from '../../theme/components/Button';
import { ThemeCard } from '../../theme/components/Card';
import ThemeContainer from '../../theme/components/Container';
import { ThemeHeadline } from '../../theme/components/Headline';
import { theme } from '../../theme/theme';
import { KanoBox } from './KanoBox';
import { KanoGrid } from './KanoGrid';

export interface StepContainerItem4Props {
  result: KanoPredictionResult | null;
}

export const StepContainerItem4: React.FC<StepContainerItem4Props> = ({ result }) => {
  return (
    <Container>
      <InnerContainer wide>
        <ThemeHeadline variant='h3' title='Ergebnis Deiner Analyse' />
        <Description>Arten von Feature-Merkmalen</Description>
        <KanoBoxGrid>
          <KanoBox
            title='Basismerkmale'
            description='Basismerkmale sind Produkteigenschaften, die vorhanden sein müssen. Aus Kundensicht stellen sie selbstverständliche Eigenschaften des Produkts oder der Dienstleistung dar.'
            color='#FFD248'
          />
          <KanoBox
            title='Unerhebliches Merkmal'
            description='Es gibt Produkteigenschaften oder Features, bei denen oft nicht klar ist, warum diese vorhanden sind. Sie spielen für die Zufriedenheit oder Unzufriedenheit des Kunden keine Rolle. Wenn sie nicht vorhanden sind, wird ihr Fehlen nicht bemerkt.'
            color='#9C9C9C'
          />
          <KanoBox
            title='Begeisterungsmerkmale (WOW-Features)'
            description='Die Begeisterungsmerkmale haben das größte Potential, den Kunden positiv zu beeinflussen. Das Angebot überrascht ihn, da er vor dem Kauf keine spezifischen Erwartungen an diese Produkteigenschaft hatte.'
            color='#6A9CD1'
          />
          <KanoBox
            title='Rückweisungsmerkmale'
            description='Hin und wieder gibt es Merkmale, die direkt zur Unzufriedenheit des Kunden führen. Im Kano Modell werden sie als Rückweisungsmerkmale bezeichnet.'
            color='#E23889'
          />
          <KanoBox
            title='Leistungsmerkmale'
            description='Leistungsmerkmale werden auch als Qualitätsmerkmale bezeichnet. Sie bieten die Chance Kundenanforderungen besser als der Wettbewerb zu erfüllen.'
            color='#36A7A3'
          />
          <KanoBox
            title='Fragwürdig'
            description='Diese Kategorie ist für Antworten gedacht, die nicht logisch sind, z. B. wenn die Zufriedenheit sowohl durch das Hinzufügen als auch durch das Entfernen eines Merkmals steigt.'
            color='#DCDDEB'
          />
        </KanoBoxGrid>
        <ThemeHeadline variant='h3' title='Dein Ergebnis' />
        <Description>{result?.summary}</Description>
        <KanoThemeCardContainer>
          <KanoThemeCard variant='secondary'>{result && <KanoGrid result={result} />}</KanoThemeCard>
        </KanoThemeCardContainer>
      </InnerContainer>
      <Footer>
        <Background src='/images/Banner.svg' alt='Google Logo' />
        <InnerFooter wide>
          <InnerInnerFooter>
            <ThemeHeadline variant='h3' title='Detaillierte Analyse durchführen?' />
            <Description>
              <div>
                Die Ergebnisse unserer KI-Analyse geben Dir einen ersten Überblick über eine mögliche Priorisierung Deiner Features.
              </div>
              <div>Möchtest Du eine tiefere Analyse, die auch Deine potenziellen Zielgruppen berücksichtigt?</div>
              <div>Dann ist das Dein nächster Schritt: Buche einen Termin mit uns, um die Ergebnisse Deiner Kano-Analyse zu vertiefen.</div>
            </Description>
            <ThemeButton
              variant='secondary'
              iconRight={<FiChevronRight />}
              onClick={() => window.open('https://services.nanogiants.de/meetings/nanogiants/discoverynow-kano', '_blank')}>
              Kano-Umfrage mit meiner Zielgruppe durchführen
            </ThemeButton>
          </InnerInnerFooter>
        </InnerFooter>
      </Footer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.l};
`;

const InnerContainer = styled(ThemeContainer)`
  gap: ${theme.spacing.l};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  margin: 120px auto;
  gap: ${theme.spacing.l};
  position: relative;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    margin: ${theme.spacing.xl} auto;
    padding: 0 ${theme.spacing.d};
    box-sizing: border-box;
  }
`;

const KanoBoxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: 84px;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: ${theme.spacing.l};
  }
`;

const KanoThemeCardContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  display: flex;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    align-self: flex-start;
  }
`;

const KanoThemeCard = styled(ThemeCard)`
  width: 100%;
  min-width: 1100px;
`;

const Description = styled.div`
  ${theme.typography.body2};
  margin-bottom: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.l};

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: left;
  position: relative;
`;

const InnerFooter = styled(ThemeContainer)``;

const InnerInnerFooter = styled.div`
  max-width: 720px;
  gap: ${theme.spacing.l};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
  padding-bottom: ${theme.spacing.xxl};

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    padding: ${theme.spacing.d};
    box-sizing: border-box;
  }
`;

const Background = styled.img`
  position: absolute;
  top: 0;
  width: 100%;
  z-index: -1;
  height: 100%;

  object-fit: cover;
`;
